<template>
  <!--
    The ROOT element takes the host's class (e.g. `upsell-btn`, `upsell-cta-btn`), so each upsell
    surface keeps its existing look — only the behaviour is shared. Two roots via v-if/v-else, never both.
  -->
  <a
    v-if="state === 'failed'"
    :href="mailtoHref"
    class="feature-request-btn is-fallback"
    data-test="feature-request-fallback"
  >
    <i class="fa fa-envelope"></i> Couldn't send. Email us instead
  </a>
  <button
    v-else
    type="button"
    class="feature-request-btn"
    :class="{ 'is-done': isDone }"
    :disabled="state !== 'idle'"
    data-test="feature-request-btn"
    @click.stop.prevent="send"
  >
    <i :class="iconClass"></i> {{ label }}
  </button>
</template>

<script>
import {
  requestFeatureEnable,
  wasRecentlyRequested,
  markRequested,
  featureRequestMailto,
} from '@/api/featureRequest';
import { FEATURE_REQUEST_BUTTON_TEXT, FEATURE_REQUEST_SENT_TEXT } from '@/utils/featureRequestCopy.js';

function safeStorage() {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch (e) {
    return null;
  }
}

export default {
  name: 'FeatureRequestButton',

  props: {
    // Must be a key in myaccount-service/lib/feature-catalog.js (every contact-us-to-enable surface).
    featureKey: { type: String, required: true },
    // Human name, used only for the mailto fallback (the server owns the email subject).
    featureLabel: { type: String, required: true },
    // Where the click happened, e.g. "Patient Profile" — shown in the email to Teamio.
    sourcePage: { type: String, required: true },
    buttonText: { type: String, default: FEATURE_REQUEST_BUTTON_TEXT },
  },

  data() {
    return {
      // idle | sending | sent | requested (sent from this browser within 24h) | failed
      state: 'idle',
    };
  },

  computed: {
    siteID() {
      return this.$store && this.$store.state.customer ? this.$store.state.customer.siteID : null;
    },
    siteName() {
      const user = this.$store && this.$store.state.customer ? this.$store.state.customer.user : null;
      return user && user.SiteName ? user.SiteName : '';
    },
    isDone() {
      return this.state === 'sent' || this.state === 'requested';
    },
    label() {
      if (this.state === 'sending') return 'Sending…';
      if (this.state === 'sent') return FEATURE_REQUEST_SENT_TEXT;
      if (this.state === 'requested') return 'Requested';
      return this.buttonText;
    },
    iconClass() {
      if (this.state === 'sending') return 'fa fa-spinner fa-spin';
      if (this.isDone) return 'fa fa-check';
      return 'fa fa-paper-plane';
    },
    mailtoHref() {
      return featureRequestMailto({ featureLabel: this.featureLabel, siteName: this.siteName, sourcePage: this.sourcePage });
    },
  },

  watch: {
    siteID: {
      immediate: true,
      handler(id) {
        if (id && this.state === 'idle' && wasRecentlyRequested(safeStorage(), id, this.featureKey)) {
          this.state = 'requested';
        }
      },
    },
  },

  methods: {
    async send() {
      if (this.state !== 'idle') return;
      if (!this.siteID) {
        this.state = 'failed';
        return;
      }
      this.state = 'sending';
      try {
        const auth = this.$store.state.getAuth ? this.$store.state.getAuth() : null;
        await requestFeatureEnable({ auth, siteID: this.siteID, featureKey: this.featureKey, sourcePage: this.sourcePage });
        markRequested(safeStorage(), this.siteID, this.featureKey);
        this.state = 'sent';
        this.$emit('sent', { featureKey: this.featureKey });
      } catch (e) {
        console.error('[FeatureRequestButton] request failed:', e);
        this.state = 'failed';
        this.$emit('failed', { featureKey: this.featureKey, error: e && e.message });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
// Neutral reset only — colour/size come from the host's class on this root.
.feature-request-btn {
  border: none;
  cursor: pointer;
  font-family: inherit;

  &:disabled {
    cursor: default;
  }

  &.is-done {
    opacity: 0.9;
  }
}
</style>
